/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useMemo } from 'react';

import { useFormik } from 'formik';

import {
  getProInfo,
  proInfo,
  updateProInfo,
  getTypeOfUser,
  userActions,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useSelfService from '../../../../hooks/useSelfService';
import useDisableProfileSave from '../../../../hooks/useDisableProfileSave';
import {
  professionalCorporatedValidator,
  ProInfoValidator,
} from '../../FormValidation';
import ProfileForm from '../../ProfileForm';
import { ICommonProfile } from '../General/General';

const handleUndefined = <T, >(
  val: string | string[] | undefined | null,
  expected: T,
): T => {
  if (val === undefined || val === null) return expected;
  return val as T;
};

const ProfessionalInfo = ({ setSelected }: ICommonProfile) => {
  const dispatch = useAppDispatch();
  const info = useAppSelector(proInfo);
  const userType = useAppSelector(getTypeOfUser);
  const { isSelfService } = useSelfService();

  const { preferredWorkingDays: contactPreferred } = info ?? {};

  const getMinusCurrentYear = (year: number) =>
    new Date().getFullYear() - year;

  const initialValue = useMemo(
    () =>
      ({
        ...info,
        incorporated: `${info?.incorporated}`,
        role: userType,
        preferredWorkingDays:
        handleUndefined<Array<string>>(contactPreferred, []).length !== 0
          ? (contactPreferred as string)?.split(',')
          : [],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactPreferred, info],
  );

  const formik = useFormik({
    initialValues: initialValue,

    onSubmit: (values) => {
      const { preferredWorkingDays } = values ?? {};
      dispatch(
        updateProInfo({
          ...values,
          incorporated: values?.incorporated === 'true',
          preferredWorkingDays: (preferredWorkingDays as Array<string>)?.join(
            ',',
          ),
        }),
      );
    },
    enableReinitialize: true,
    validationSchema: isSelfService
      ? professionalCorporatedValidator()
      : ProInfoValidator(),
  });

  useEffect(() => {
    formik.setFieldValue(
      'yearsOfExperience',
      getMinusCurrentYear(parseInt(formik.values.graduationYear, 10)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.graduationYear]);

  useEffect(() => {
    dispatch(getProInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    formik.setValues({
      ...info,
      role: userType,
      englishLevel: info.englishLevel.toString(),
      incorporated: `${info?.incorporated}`,
      frenchLevel: info.frenchLevel.toString(),
      preferredWorkingDays:
        handleUndefined<Array<string>>(contactPreferred, []).length !== 0
          ? (contactPreferred as string)?.split(',')
          : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const { disabled } = useDisableProfileSave({
    action: userActions.professionalInformation_update,
    mutableValues: formik.values,
    initialValue,
  });

  return (
    <ProfileForm
      setSelected={setSelected}
      selected="professionalInfo"
      formik={formik}
      disableSave={disabled}
    />
  );
};

export default ProfessionalInfo;
